import useInfiniteScroll from "react-infinite-scroll-hook";
import { useIntl } from "react-intl";

import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { TripBookingCard } from "@/features/trips/components/bookingDetails/TripBookingCard";
import { useTripBookings } from "@/features/trips/hooks/useTripBookings";

export const MyTripsList = ({
  onReservationClick,
}: {
  onReservationClick: (reservationId: string) => void;
}) => {
  const intl = useIntl();
  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage } =
    useTripBookings();
  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: () => {
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  if (isLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="trip" />
      </PageLoader>
    );
  }

  const bookingCount = data?.pages?.[0]?.data?.count;
  const bookings =
    data?.pages?.flatMap((page) => page.data.packageBookings) || [];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        {/* a spacer to align the booking count text */}
        <div />

        {bookingCount ? (
          <div className="text-md font-medium">
            {intl.formatMessage(
              {
                defaultMessage:
                  "{bookingCount, plural, one {# booking} other {# bookings}}",
                description: "hotels.bookedHotels",
              },
              { bookingCount },
            )}
          </div>
        ) : null}
      </div>

      {bookings.length > 0 ? (
        <ul
          aria-label={intl.formatMessage({
            defaultMessage: "Booked hotels list",
            description: "hotels.bookedHotelsList",
          })}
          className="space-y-2"
        >
          {bookings.map((booking) => (
            <li key={booking.id}>
              <TripBookingCard
                booking={booking}
                onClick={() => onReservationClick(booking.id)}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {isFetching || hasNextPage ? (
        <div
          className="flex items-center justify-center py-4"
          ref={sentryRef}
          aria-hidden={!isFetching}
        >
          <AnimatedLoader icon="trip" />
        </div>
      ) : (
        <div className="flex items-center justify-center p-8 text-text-tertiary">
          {intl.formatMessage({
            defaultMessage: "No more results",
            description: "hotels.noMoreResults",
          })}
        </div>
      )}
    </>
  );
};
